<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Page Header -->
    <PageHeader
      title="Informations générales"
      subtitle="Gérez vos informations personnelles et professionnelles"
    >
    </PageHeader>


    <div class="w-full md:px-6 py-8">
      <!-- Indicateur de chargement -->
      <div v-if="isLoading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span class="ml-3 text-black">Chargement des données...</span>
      </div>

      <!-- Contenu principal -->
      <div v-else-if="formData">
      <!-- Blocs de navigation - Desktop -->
      <div class="hidden md:grid grid-cols-5 gap-4 mb-8">
        <div 
          @click="handleSectionClick('account')"
          :class="[
            'cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 transform hover:scale-105',
            activeSection === 'account'
              ? 'border-blue-500 bg-blue-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
          ]"
        >
          <div class="text-center">
            <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i class="fas fa-user text-blue-600 text-xl"></i>
            </div>
            <h3 class="font-semibold text-black text-sm">Paramètres du compte</h3>
          </div>
        </div>

        <div 
          @click="handleSectionClick('addresses')"
          :class="[
            'cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 transform hover:scale-105',
            activeSection === 'addresses'
              ? 'border-green-500 bg-green-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-green-300 hover:shadow-md'
          ]"
        >
          <div class="text-center">
            <div class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i class="fas fa-map-marker-alt text-green-600 text-xl"></i>
            </div>
            <h3 class="font-semibold text-black text-sm">Adresses</h3>
          </div>
        </div>

        <div 
          @click="handleSectionClick('leaves')"
          :class="[
            'cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 transform hover:scale-105',
            activeSection === 'leaves'
              ? 'border-purple-500 bg-purple-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-md'
          ]"
        >
          <div class="text-center">
            <div class="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i class="fas fa-calendar-alt text-purple-600 text-xl"></i>
            </div>
            <h3 class="font-semibold text-black text-sm">Gestion des congés</h3>
          </div>
        </div>

        <div 
          @click="handleSectionClick('images')"
          :class="[
            'cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 transform hover:scale-105',
            activeSection === 'images'
              ? 'border-orange-500 bg-orange-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-orange-300 hover:shadow-md'
          ]"
        >
          <div class="text-center">
            <div class="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i class="fas fa-images text-orange-600 text-xl"></i>
            </div>
            <h3 class="font-semibold text-black text-sm">Images et médias</h3>
          </div>
        </div>

        <div 
          @click="handleSectionClick('description')"
          :class="[
            'cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 transform hover:scale-105',
            activeSection === 'description'
              ? 'border-cyan-500 bg-cyan-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-cyan-300 hover:shadow-md'
          ]"
        >
          <div class="text-center">
            <div class="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i class="fas fa-globe text-cyan-600 text-xl"></i>
            </div>
            <h3 class="font-semibold text-black text-sm">Description multi-langues</h3>
          </div>
        </div>
      </div>

      <!-- Blocs de navigation - Mobile -->
      <div class="relative md:hidden mb-8">
        <!-- Flèche gauche -->
        <button 
          @click="scrollNav('left')"
          class="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 shadow-md rounded-full flex items-center justify-center text-black hover:text-black hover:bg-white transition-all"
        >
          <i class="fas fa-chevron-left text-sm"></i>
        </button>

        <!-- Flèche droite -->
        <button 
          @click="scrollNav('right')"
          class="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 shadow-md rounded-full flex items-center justify-center text-black hover:text-black hover:bg-white transition-all"
        >
          <i class="fas fa-chevron-right text-sm"></i>
        </button>

        <!-- Conteneur scrollable -->
        <div 
          ref="mobileNavRef"
          class="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide mx-10"
        >
        <div 
          @click="handleSectionClick('account')"
          :class="[
            'cursor-pointer p-3 rounded-xl border-2 transition-all duration-200 w-[120px] flex-shrink-0 snap-center',
            activeSection === 'account'
              ? 'border-blue-500 bg-blue-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
          ]"
        >
          <div class="text-center">
            <div class="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <i class="fas fa-user text-blue-600 text-base"></i>
            </div>
            <h3 class="font-semibold text-black text-xs">Paramètres du compte</h3>
          </div>
        </div>

        <div 
          @click="handleSectionClick('addresses')"
          :class="[
            'cursor-pointer p-3 rounded-xl border-2 transition-all duration-200 w-[120px] flex-shrink-0 snap-center',
            activeSection === 'addresses'
              ? 'border-green-500 bg-green-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-green-300 hover:shadow-md'
          ]"
        >
          <div class="text-center">
            <div class="w-9 h-9 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <i class="fas fa-map-marker-alt text-green-600 text-base"></i>
            </div>
            <h3 class="font-semibold text-black text-xs">Adresses</h3>
          </div>
        </div>

        <div 
          @click="handleSectionClick('leaves')"
          :class="[
            'cursor-pointer p-3 rounded-xl border-2 transition-all duration-200 w-[120px] flex-shrink-0 snap-center',
            activeSection === 'leaves'
              ? 'border-purple-500 bg-purple-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-md'
          ]"
        >
          <div class="text-center">
            <div class="w-9 h-9 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <i class="fas fa-calendar-alt text-purple-600 text-base"></i>
            </div>
            <h3 class="font-semibold text-black text-xs">Gestion des congés</h3>
          </div>
        </div>

        <div 
          @click="handleSectionClick('images')"
          :class="[
            'cursor-pointer p-3 rounded-xl border-2 transition-all duration-200 w-[120px] flex-shrink-0 snap-center',
            activeSection === 'images'
              ? 'border-orange-500 bg-orange-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-orange-300 hover:shadow-md'
          ]"
        >
          <div class="text-center">
            <div class="w-9 h-9 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <i class="fas fa-images text-orange-600 text-base"></i>
            </div>
            <h3 class="font-semibold text-black text-xs">Images et médias</h3>
          </div>
        </div>

        <div 
          @click="handleSectionClick('description')"
          :class="[
            'cursor-pointer p-3 rounded-xl border-2 transition-all duration-200 w-[120px] flex-shrink-0 snap-center',
            activeSection === 'description'
              ? 'border-cyan-500 bg-cyan-50 shadow-lg' 
              : 'border-gray-200 bg-white hover:border-cyan-300 hover:shadow-md'
          ]"
        >
          <div class="text-center">
            <div class="w-9 h-9 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <i class="fas fa-globe text-cyan-600 text-base"></i>
            </div>
            <h3 class="font-semibold text-black text-xs">Description multi-langues</h3>
          </div>
        </div>
        </div>
      </div>

      <!-- Point d'ancrage pour le scroll  -->
      <div id="general-info-anchor" class="scroll-mt-4"></div>
      

      <!-- Message d'information -->
      <div class="mb-4">
        <div class="alert-save mb-2">
          <i class="fas fa-info-circle"></i>
          <p>
            <strong>N'oubliez pas :</strong> Appuyez sur "Sauvegarder" pour enregistrer vos modifications.
          </p>
        </div>
    
      </div>

      <div class="space-y-6">
                <!-- Section Paramètres du compte -->
        <div v-show="activeSection === 'account'">
          <AccountSettingsSection 
            :form-data="formData" 
            :saving="isSaving"
            :disable-save="hasAccountErrors"
            :error-message="accountError"
            @save="saveAccount"
            @update:form-data="formData = $event"
            @validation-changed="handleValidationChanged"
          />
        </div>

        <!-- Section Adresses -->
        <div v-show="activeSection === 'addresses'">
          <AddressesSection 
            :form-data="formData" 
            :saving="isSaving"
            :disable-save="hasAddressErrors"
            @save="saveAddresses"
            @update:form-data="formData = $event"
            @validation-changed="handleAddressValidationChanged"
          />
        </div>

        <!-- Section Gestion des congés -->
        <div v-show="activeSection === 'leaves'">
          <LeavesSection 
            :form-data="formData" 
            :saving="isSaving"
            :disable-save="false"
            @save="saveLeaves"
            @add-leave="saveLeavesWith"
            @update:form-data="formData = $event" 
          />
        </div>

        <!-- Section Images et médias -->
        <div v-show="activeSection === 'images'">
          <ImagesSection 
            ref="imagesSectionRef" 
            :image-urls="imageUrls"
            :saving="isSaving"
            :disable-save="false"
            @save="saveImages"
            @images-changed="handleImagesChanged"
            @delete-image="handleDeleteImage"
          />
        </div>

        <!-- Section Description multi-langues -->
        <div v-show="activeSection === 'description'">
          <DescriptionSection 
            :form-data="formData" 
            :saving="isSaving"
            :disable-save="false"
            @save="saveDescriptions"
            @update:form-data="formData = $event" 
          />
        </div>



      </div>
      </div>
    </div>

    <!-- Modal d'avertissement des champs requis -->
    <ConfirmModal
      :show="showValidationModal"
      title="Champs obligatoires manquants"
      message="Veuillez remplir tous les champs obligatoires (étoile rouge)."
      confirm-text="OK"
      @confirm="showValidationModal = false"
      @cancel="showValidationModal = false"
    />
            
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, watch, computed, nextTick } from 'vue'
import type { GeneralInformationFormData, ParsedVendeurData, VendeurApiData, ImageUrls, UploadedImages } from '@/types'
import { 
  PageHeader, 
  AccountSettingsSection,
  AddressesSection,
  LeavesSection,
  ImagesSection,
  DescriptionSection,
  ConfirmModal
} from '@/components'
import { 
  getVendeurGeneralInfo, 
  updateVendeurGeneralInfo,
  saveVendeurAccountSettings,
  saveVendeurAddresses,
  saveVendeurLeaves,
  saveVendeurImages,
  saveVendeurDescriptions,
  deleteVendeurImage
} from '@/api/vendeur'
import { useNotification } from '@/composables/useNotification'
import { getSellerImageUrl } from '@/config/api'
import {useAuthStore} from "@/stores/auth";
import {useRouter} from "vue-router";

// État réactif
const isSaving = ref(false)
const isLoading = ref(false)
const activeSection = ref('account') 
const formData = ref<GeneralInformationFormData | null>(null)
const imagesSectionRef = ref() 
const mobileNavRef = ref<HTMLElement | null>(null)
const uploadedImages = ref<UploadedImages>({ logo: null, banniere: null, square: null })
const imageUrls = ref<ImageUrls>({})
const authStore = useAuthStore()
const router = useRouter()
const showValidationModal = ref(false)
// Notifications
const { success, error } = useNotification()
const hasValidationErrors = ref(false)
const hasAccountErrors = ref(false)
const hasAddressErrors = ref(false)
// Message d'erreur détaillé pour la section Paramètres du compte
const accountError = ref('')

// Suivi des modifications non sauvegardées
const originalSnapshot = ref<string>('')
const serializeState = () => {
  return JSON.stringify({
    formData: formData.value,
    images: {
      logo: uploadedImages.value.logo ? (uploadedImages.value.logo as any).name || true : null,
      banniere: uploadedImages.value.banniere ? (uploadedImages.value.banniere as any).name || true : null,
      square: uploadedImages.value.square ? (uploadedImages.value.square as any).name || true : null,
    }
  })
}
const takeSnapshot = () => { originalSnapshot.value = serializeState() }
const hasUnsavedChanges = computed(() => {
  if (!formData.value) return false
  return serializeState() !== originalSnapshot.value
})

// Méthodes
const scrollNav = (direction: 'left' | 'right') => {
  if (!mobileNavRef.value) return
  const scrollAmount = 130
  mobileNavRef.value.scrollBy({
    left: direction === 'left' ? -scrollAmount : scrollAmount,
    behavior: 'smooth'
  })
}

const handleImagesChanged = (images: UploadedImages) => {
  uploadedImages.value = images
}

const handleDeleteImage = async (type: 'banniere' | 'square' | 'logo-boutique') => {
  try {
    await deleteVendeurImage(type)
    // Mettre à jour les URLs locales pour masquer l'image supprimée
    imageUrls.value = { ...imageUrls.value, [type]: '' }
    success('Image supprimée avec succès')
  } catch (e) {
    error('Erreur lors de la suppression de l\'image')
  }
}

const handleValidationChanged = (hasErrors: boolean) => {
  hasAccountErrors.value = hasErrors
  updateGlobalValidation()
}

const handleAddressValidationChanged = (hasErrors: boolean) => {
  hasAddressErrors.value = hasErrors
  updateGlobalValidation()
}

const updateGlobalValidation = () => {
  hasValidationErrors.value = hasAccountErrors.value || hasAddressErrors.value
}

// Nettoyer le message d'erreur quand l'utilisateur modifie les champs
watch(() => formData.value, () => {
  accountError.value = ''
}, { deep: true })
watch(() => uploadedImages.value, () => {
  // images modifiées => toujours considérer comme changements
}, { deep: true })

const saveForm = async () => {
  //éviter le double envoi
  if (isSaving.value || !formData.value) {
    return
  }
  // Bloquer si des champs requis manquent et afficher un pop-up
  if (hasValidationErrors.value) {
    showValidationModal.value = true
    return
  }
  
  isSaving.value = true
  
  try {
    const result = await updateVendeurGeneralInfo(formData.value, uploadedImages.value)
    
    if (result) {
      success('Vos informations ont été sauvegardées avec succès !')
      // Recharger les données depuis l'API pour s'assurer du retour en base + rafraîchir les visuels
      await loadVendeurData()
    } else {
      error('Une erreur est survenue lors de la sauvegarde')
    }
  } catch (err) {
    error('Une erreur est survenue lors de la sauvegarde')
  } finally {
    isSaving.value = false
  }
}

// Sauvegardes par section
const saveAccount = async () => {
  if (isSaving.value || !formData.value) return
  if (hasAccountErrors.value) { showValidationModal.value = true; return }
  isSaving.value = true
  try {
    const ok = await saveVendeurAccountSettings(formData.value)
    if (ok) {
      success('Paramètres du compte sauvegardés ✔')
      await loadVendeurData()
      accountError.value = ''
    }
  } catch (e) {
    const msg = extractErrorMessage(e)
    accountError.value = msg
    error(msg)
  } finally {
    isSaving.value = false
  }
}

const saveAddresses = async () => {
  if (isSaving.value || !formData.value) return
  if (hasAddressErrors.value) { showValidationModal.value = true; return }
  isSaving.value = true
  try {
    const ok = await saveVendeurAddresses(formData.value)
    if (ok) {
      success('Adresses sauvegardées ✔')
      await loadVendeurData()
    }
  } catch (e) {
    console.error(e)
    error(extractErrorMessage(e))
  } finally {
    isSaving.value = false
  }
}

const saveLeaves = async () => {
  if (isSaving.value || !formData.value) return
  isSaving.value = true
  try {
    const ok = await saveVendeurLeaves(formData.value)
    if (ok) {
      success('Congés sauvegardés ✔')
      await loadVendeurData()
    } else {
      error('Échec de la sauvegarde des congés')
    }
  } catch (e) {
    console.error(e)
    error(extractErrorMessage(e))
  } finally {
    isSaving.value = false
  }
}

// Ajout d'un congé : on reçoit directement les données mises à jour et on
// sauvegarde puis on rafraîchit. Si une sauvegarde est déjà en cours, on
// attend qu'elle se termine au lieu d'ignorer silencieusement la demande
// (c'est ce qui faisait que l'ajout n'était parfois pas pris en compte).
const saveLeavesWith = async (data: GeneralInformationFormData) => {
  formData.value = data

  if (isSaving.value) {
    await new Promise<void>((resolve) => {
      const stop = watch(isSaving, (v) => {
        if (!v) {
          stop()
          resolve()
        }
      })
    })
  }

  isSaving.value = true
  try {
    const ok = await saveVendeurLeaves(formData.value)
    if (ok) {
      success('Congé ajouté ✔')
      await loadVendeurData()
    } else {
      error("Échec de l'ajout du congé")
    }
  } catch (e) {
    console.error(e)
    error(extractErrorMessage(e))
  } finally {
    isSaving.value = false
  }
}

const saveImages = async () => {
  if (isSaving.value) return
  isSaving.value = true
  try {
    const ok = await saveVendeurImages(uploadedImages.value)
    if (ok) {
      success('Images sauvegardées ✔')
      await loadVendeurData()
    }
  } catch (e) {
    console.error(e)
    error(extractErrorMessage(e))
  } finally {
    isSaving.value = false
  }
}

const saveDescriptions = async () => {
  if (isSaving.value || !formData.value) return
  isSaving.value = true
  try {
    const ok = await saveVendeurDescriptions(formData.value)
    if (ok) {
      success('Descriptions sauvegardées ✔')
      await loadVendeurData()
    }
  } catch (e) {
    console.error(e)
    error(extractErrorMessage(e))
  } finally {
    isSaving.value = false
  }
}

// Normaliser la valeur du centre pour correspondre aux options du select
const normalizeCenterValue = (value: string): string => {
  if (!value) return ''
  const normalized = value.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_')
  const knownValues = ['aeroville', 'cifa', 'fashion_center', 'lem888', 'silk_road_paris']
  if (knownValues.includes(normalized)) return normalized
  // Essayer une correspondance partielle
  const match = knownValues.find(k => k === value.toLowerCase() || value.toLowerCase().includes(k.replace(/_/g, ' ')) || k.replace(/_/g, ' ').includes(value.toLowerCase()))
  return match || value
}

// Fonction pour charger les données depuis l'API
const loadVendeurData = async () => {
  isLoading.value = true
  try {
    const data = await getVendeurGeneralInfo()
    
    // Parser les données JSON stockées dans vendeurData.data
    let parsedData: ParsedVendeurData = {}
    const apiData = data as unknown as VendeurApiData

    if (apiData.vendeurData?.data) {
      try {
        parsedData = JSON.parse(apiData.vendeurData.data) as ParsedVendeurData
      } catch (error) {
        // Erreur silencieuse lors du parsing
      }
    }
    
    // Récupérer les adresses par type
    const storeAddress = apiData?.adresses?.find(addr => addr.type === 2) // type=2 = magasin
    const billingAddress = apiData?.adresses?.find(addr => addr.type === 3) // type=3 = facturation
    

    
    // Trier les éventuels doublons historiques afin de relire la ligne la plus
    // récente, comme le site acheteur.
    const languageRows = [...(apiData?.vendeursLangues || [])].sort(
      (left, right) => Number(left.id_vendeur_langue) - Number(right.id_vendeur_langue),
    )
    const baselineData: Record<string, string> = languageRows.reduce((acc, lang) => {
      acc[lang.id_langue.toLowerCase()] = lang.baseline || ''
      return acc
    }, {} as Record<string, string>) || {}
    
    const descriptionData: Record<string, string> = languageRows.reduce((acc, lang) => {
      acc[lang.id_langue.toLowerCase()] = lang.description || ''
      return acc
    }, {} as Record<string, string>) || {}
    
    // Récupérer les congés (actifs et supprimés)
    const leavesData: Array<{ id?: number; startDate: string; endDate: string; deleted?: boolean }> = apiData?.vendeursConges?.map(conge => ({
      id: conge.id_vendeur_conge,
      startDate: conge.debut_conge,
      endDate: conge.fin_conge,
      deleted: conge.supprimer === 1
    })) || []
    
    // Générer les URLs des images
    const userId = (apiData as any)?.id_vendeur
    if (userId) {
      const ts = Date.now()
      imageUrls.value = {
        logo    : getSellerImageUrl(`/uploads/sellers/${userId}/logo-boutique.jpg?t=${ts}`),
        banniere: getSellerImageUrl(`/uploads/sellers/${userId}/banniere.jpg?t=${ts}`),
        square  : getSellerImageUrl(`/uploads/sellers/${userId}/square.jpg?t=${ts}`)
      }
    }

    // formData directement avec les données du vendeur
    formData.value = reactive<GeneralInformationFormData>({
      firstName: apiData?.prenomContact || '',
      lastName: apiData?.nomContact || '',
      email: apiData?.email || '',
      emailSecondary: apiData?.email2 || '',
      minOrder: apiData?.commandeMini || 0,
      website: apiData?.siteWeb || '',
      socialMedia: {
        facebook: (typeof parsedData['social-url'] === 'object' && parsedData['social-url']?.facebook) || '',
        twitter: (typeof parsedData['social-url'] === 'object' && parsedData['social-url']?.twitter) || '',
        instagram: (typeof parsedData['social-url'] === 'object' && parsedData['social-url']?.instagram) || '',
        tiktok: (typeof parsedData['social-url'] === 'object' && parsedData['social-url']?.tiktok) || ''
      },
      baseline: {
        fr: baselineData?.fr || '',
        en: baselineData?.en || '',
        es: baselineData?.es || '',
        it: baselineData?.it || ''
      },
      banking: {
        iban: apiData?.vendeurData?.iban || (Array.isArray(parsedData.iban) ? parsedData.iban.join('') : (parsedData.iban || '')),
        bic: parsedData.bic || '',
        accountTitle: apiData?.vendeurData?.accountTitle || ''
      },
      security: {
        changePassword: false,
        newPassword: '',
        confirmPassword: ''
      },
      shop: {
        status: apiData?.statut === 1 ? 'online' : 'offline',
        catalogVisibility: apiData?.mea_month_delay?.toString() || '0',
        smsNotification: apiData?.vendeurData?.groupage === 1,
        showMainColor: apiData?.vendeurData?.displayColormain === 1
      },
      storeAddress: {
        storeName: storeAddress?.NomContact || apiData?.nomBoutique || '',
        number: storeAddress?.numero || '',
        street: storeAddress?.rue ? `${storeAddress?.numero || ''} ${storeAddress?.rue}`.trim() : storeAddress?.adresse || '',
        complement: storeAddress?.complement || '',
        center: normalizeCenterValue(storeAddress?.centre || ''),
        building: storeAddress?.batiment || '',
        lot: storeAddress?.immeuble || '',
        city: storeAddress?.ville || '',
        postalCode: storeAddress?.codePostal || '',
        phone: storeAddress?.telephone || '',
        mobile: storeAddress?.mobile || ''
      },
      billingAddress: {
        company: billingAddress?.Societe || apiData?.nomSociete || '',
        number: billingAddress?.numero || '',
        street: billingAddress?.rue ? `${billingAddress?.numero || ''} ${billingAddress?.rue}`.trim() : billingAddress?.adresse || '',
        city: billingAddress?.ville || '',
        postalCode: billingAddress?.codePostal || '',
        phone: billingAddress?.telephone || '',
        siret: apiData?.siret || '',
        tva: apiData?.tva || '',
        eori: apiData?.vendeurData?.eori || '',
        sameAsStore: false
      },
      leaves: leavesData,
      description: {
        fr: descriptionData?.fr || '',
        en: descriptionData?.en || '',
        es: descriptionData?.es || '',
        it: descriptionData?.it || ''
      }
    })
    // snapshot après chargement (attendre le rendu pour éviter les faux positifs)
    await nextTick()
    takeSnapshot()
    
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue'
    error(`Erreur lors du chargement des données: ${errorMessage}`)
  } finally {
    isLoading.value = false
  }
}

// Gestion changement de section avec avertissement de sauvegarde
const saveCurrentSection = async () => {
  if (!formData.value) return
  switch (activeSection.value) {
    case 'account':
      await saveAccount(); break
    case 'addresses':
      await saveAddresses(); break
    case 'leaves':
      await saveLeaves(); break
    case 'images':
      await saveImages(); break
    case 'description':
      await saveDescriptions(); break
  }
}

const handleSectionClick = async (target: string) => {
  if (activeSection.value === target) return
  // Plus de popup natif; on change simplement de section
  activeSection.value = target
  
  // Scroll vers le point d'ancrage en bas
  await nextTick()
  setTimeout(() => {
    const anchor = document.getElementById('general-info-anchor')
    if (anchor) {
      anchor.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, 100)
}

// Extraction message d'erreur lisible
const extractErrorMessage = (e: unknown): string => {
  if (!e) return 'Échec de la sauvegarde (raison inconnue)'
  if (e instanceof Error) return e.message
  try { return JSON.stringify(e) } catch { return String(e) }
}

// Lifecycle
onMounted(async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth()
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: 'auth' })
    return
  }
  loadVendeurData()
})
</script>

<style scoped>
/* Alerte de sauvegarde plus visible */
.alert-save {
  background: #FFF7E6;          /* fond clair orangé */
  border: 1px solid #FAAD14;    /* bordure orange */
  border-left-width: 4px;        /* accent à gauche */
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #AD4E00;               /* texte plus lisible */
}

.alert-save i {
  margin-right: 8px;
  font-size: 18px;
  line-height: 1;              /* évite le décalage vertical */
  vertical-align: middle;      /* aligne l'icône au milieu de la ligne */
  display: inline-flex;        /* permet un centrage fiable */
  align-items: center;
  justify-content: center;
}

/* Supprime les marges par défaut qui créent un décalage vertical */
.alert-save p {
  margin: 0;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
